import { Component, EventEmitter, Input, Output } from "@angular/core";
import { NavController, NavParams, ViewController } from "ionic-angular";
import {
  DropdownItem,
  Order,
  OrderComponent,
  OrderVisitFrequency,
} from "models/order-management/models";
import { OrderFormState } from "pages/orders-entry-om/add-order/order-form-state";
import { Subject } from "rxjs";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import {
  OrderComponentInterface,
  OrderComponentPropertyUpdate,
  OrderCreateRequestUpdate,
} from "../order-components-interface";

@Component({
  selector: "ordering-dropdown",
  templateUrl: "dropdown.html",
})
export class OrderingDropdownComponent implements OrderComponentInterface {
  /****************************************************************************
   * Inputs                                                                   *
   ****************************************************************************/
  @Input() orderComponent: OrderComponent;
  @Input() orderCreateRequest: Order;
  @Input() get orderFormState(): OrderFormState {
    return this._orderFormState;
  }

  /****************************************************************************
   * Outputs                                                                  *
   ****************************************************************************/
  @Output() orderCreateRequestEvent =
    new EventEmitter<OrderCreateRequestUpdate>();
  @Output() orderFormStateEvent = new EventEmitter<OrderFormState>();
  @Output() isValidEvent = new EventEmitter<boolean>();
  @Output() amendedOrderRequestEvent =
    new EventEmitter<OrderComponentPropertyUpdate>();

  /****************************************************************************
   * State                                                                    *
   ****************************************************************************/
  dropdownItems: DropdownItem[] = [];
  itemsQuery$ = new Subject<string>();
  selectedItem: string;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public viewCtrl: ViewController,
    public stateData: StateManagementService
  ) {}
  private _orderFormState: OrderFormState;

  /****************************************************************************
   * Lifecycle Methods                                                        *
   ****************************************************************************/
  ngOnInit() {
    this.itemsSearch();
    this.checkIsValid();
    const updateObject: OrderCreateRequestUpdate = { updates: {} };
    if (this.orderFormState.declined) {
      const componentField = this.orderComponent.properties[0].name;
      updateObject.updates[componentField] = this.selectedItem;
      this.orderCreateRequestEvent.emit(updateObject);
    }
  }

  /****************************************************************************
   * Getter Methods                                                           *
   ****************************************************************************/
  get isDisabled() {
    if ([0, 1].includes(this.dropdownItems.length)) {
      return true;
    }
    return false;
  }

  public get shouldShow() {
    const componentField = this.orderComponent.properties[0].name;
    if (componentField === "discipline") {
      return true;
    } else if (componentField === "coverageDesignation") {
      return this._orderFormState.hospicePays === false;
    }
    return !this._orderFormState.declined;
  }

  /****************************************************************************
   * Setter Methods                                                           *
   ****************************************************************************/
  set orderFormState(orderFormState: OrderFormState) {
    const componentField = this.orderComponent.properties[0].name;
    const shouldClear = OrderFormState.shouldClear(
      componentField,
      this._orderFormState,
      orderFormState
    );
    this._orderFormState = orderFormState;
    if (shouldClear && !!this.selectedItem) {
      this.selectedItem = undefined;
      this.orderCreateRequestEvent.emit({
        deletes: [componentField as keyof Order],
      });
      this.orderFormStateEvent.emit({
        [componentField]: undefined,
      });
    }
    this.itemsSearch();
    this.checkIsValid();
  }

  /****************************************************************************
   * Component Related Methods                                                *
   ****************************************************************************/
  onItemSelected(event: string) {
    if (event) {
      const updateObject: OrderCreateRequestUpdate = { updates: {} };
      const componentField = this.orderComponent.properties[0].name;
      switch (componentField) {
        case "diagnosisType":
          updateObject.updates[componentField] = Order.DiagnosisTypeEnum[event];
          break;
        case "visits":
        case "duration":
          let value = parseInt(event);
          updateObject.updates[componentField] = value;
          this.orderFormStateEvent.emit({
            [componentField]: value,
          });
          break;
        case "timeInterval":
        case "durationInterval":
          this.orderFormStateEvent.emit({
            [componentField]: event,
          });
          updateObject.updates[componentField] =
            event as Order.TimeIntervalEnum;
          break;
        case "discipline":
          this.orderFormStateEvent.emit({
            discipline: event,
          });
          updateObject.updates[componentField] = event;
          break;
        default:
          updateObject.updates[componentField] = event;
          break;
      }
      this.orderCreateRequestEvent.emit(updateObject);
    }
    this.checkIsValid();
  }

  itemsSearch() {
    this.dropdownItems = this.orderComponent.properties[0].dropdownItems;

    // Filters based on other fields in the form state
    if (this.orderComponent.properties[0].name === "durationInterval") {
      this.dropdownItems = this.dropdownItems.filter((item) =>
        item.filterValues?.includes(this.orderFormState.timeInterval)
      );
    }

    // If timeInterval is 2_WEEK
    // and durationInterval is WEEK
    // and visits is odd
    // then duration must be even
    // otherwise there will be a fractional visit count (i.e., half a visit)
    if (
      this.orderComponent.properties[0].name === "duration" &&
      this.orderFormState.timeInterval === "2_WEEKS" &&
      this.orderFormState.durationInterval === "WEEK" &&
      this.orderFormState.visits % 2 !== 0
    ) {
      this.dropdownItems = this.dropdownItems.filter(
        (item) => parseInt(item.value) % 2 === 0
      );
    }

    this.preselectValue();
  }

  checkIsValid() {
    const isSelectionEmpty =
      !this.selectedItem || this.selectedItem.length === 0;

    if (this.isRequired() && isSelectionEmpty) {
      const isDeclined = this.orderFormState.declined;
      this.isValidEvent.emit(isDeclined || false);
    } else {
      this.isValidEvent.emit(true);
    }
  }

  isRequired() {
    const componentField = this.orderComponent.properties[0].name;
    const { declined, PRN } = this._orderFormState;
    // All four of these fields are required unless declined or
    // PRN Only order
    if (
      ["visits", "timeInterval", "duration", "durationInterval"].includes(
        componentField
      )
    ) {
      const notRequired =
        declined || OrderVisitFrequency.isPRNOnlyOrder(this._orderFormState);
      return !notRequired;
    }
    return this.orderComponent.properties[0].required && !this.isDisabled;
  }

  onItemClear(event) {
    this.selectedItem = "";
    const componentField = this.orderComponent.properties[0].name;
    switch (componentField) {
      case "visits":
      case "duration":
        this.orderFormStateEvent.emit({
          [componentField]: 0,
        });
        break;
      case "timeInterval":
      case "durationInterval":
      case "discipline":
        this.orderFormStateEvent.emit({
          [componentField]: "",
        });
        break;
      default:
        break;
    }
    this.orderCreateRequestEvent.emit({
      deletes: [componentField as keyof Order],
    });
    this.checkIsValid();
  }

  preselectValue() {
    const oldValue = this.selectedItem;
    // One item, select it
    if (this.dropdownItems.length == 1) {
      this.selectedItem = this.dropdownItems[0].value;
    }
    // No items, clear selection
    else if (this.dropdownItems.length == 0) {
      this.selectedItem = "";
    }
    // Current selection not in items
    if (!this.dropdownItems.some((item) => item.value === this.selectedItem)) {
      this.selectedItem = "";
    }
    // Update
    if (oldValue !== this.selectedItem) {
      if (!this.selectedItem) {
        this.onItemClear(undefined);
      } else {
        this.onItemSelected(this.selectedItem);
      }
    }
  }

  /****************************************************************************
   * Cypress-Related ("data-cy") Methods                                      *
   ****************************************************************************/
  toKebabCase(label: string): string {
    if (label) {
      return label
        .replace(/^\s+|\s+$/gm, "")
        .replace(/([a-z])([A-Z])/g, "$1-$2")
        .replace(/[\s_]+/g, "-")
        .toLowerCase();
    }
    return "";
  }

  createDataCyAttribute(component: string, option: string): string {
    return `${this.toKebabCase(component)}-option-${this.toKebabCase(option)}`;
  }
}



// WEBPACK FOOTER //
// ./src/components/order-management/dropdown/dropdown.ts